/**
 * Editorial art and copy for the hero cards.
 *
 * The catalogue's own thumbnails were made by different people at different
 * times, so a row of them reads as noise rather than as one shop. These
 * replace them with stills shot to one fixed recipe — same key light, same
 * lens, same palette — which is what makes the set look designed rather than
 * collected.
 *
 * Products are matched on a normalised title rather than on the Tebex slug,
 * because Tebex returns a null slug for these packages and the storefront
 * derives its own. A product with no entry here simply keeps its existing
 * thumbnail, so the catalogue never breaks while the set is being filled in.
 *
 * The blurb is one sentence, two at most. It sits on the card at a size where
 * a third sentence would not be read.
 */

const normalise = (title) => String(title || '')
  .toLowerCase()
  .replace(/\[[^\]]*\]/g, ' ')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/* The families a card name can be set in. Setting every card in one face is
   what made the first pass look like a template rather than a set — the shop
   sells radio scripts and police jobs and racing bars, and they should not all
   speak in the same voice. What holds them together is the layout, the palette
   and the light, not one typeface.

   `size` is the name's font-size in cqw, tuned per family: these faces differ
   enormously in how much they set per em, so a single figure would make the
   condensed ones tiny and the wider ones overflow. */
export const FACES = {
  /* Eight faces became six files, and two of the six were already being
     loaded for the interface anyway — the home page was fetching 280 KB of
     type, more than its entire image payload. The variety that matters here
     is the setting, not the file: `condensed` is the same family as `tall`
     at a different weight and size, and reads as a different treatment
     because that is what actually distinguishes them at this scale. */
  tall: { family: 'retro5', weight: 200, size: 15, tracking: '0.012em' },
  condensed: { family: 'retro5', weight: 500, size: 13.2, tracking: '0.02em' },
  grotesque: { family: 'retro6', weight: 400, size: 9.2, tracking: '-0.02em' },
  rounded: { family: 'retro3', weight: 400, size: 8.4, tracking: '-0.035em' },
  /* The interface sans, used as a display face: light, large, tracking pulled
     right in. Same recipe as the hero headline. */
  neutral: { family: 'retro1', weight: 300, size: 7.6, tracking: '-0.05em' },
  /* Light weight, tracking pulled in hard. On a geometric sans that is the
     whole modern-editorial effect — the face matters less than the setting. */
  geometric: { family: 'retro8', weight: 300, size: 8.2, tracking: '-0.045em' },
  /* The serif voice, for names that should read expensive rather than
     technical. High contrast, so it wants a little more size to hold up. */
  serif: { family: 'retro10', weight: 300, size: 8.8, tracking: '-0.025em' },
  /* One serif file, two settings. These two were separate families doing the
     same job — a quiet, high-contrast voice — and the second one alone cost
     49 KB on the home page. */
  serifQuiet: { family: 'retro10', weight: 400, size: 8.4, tracking: '-0.008em' },
};

/* Keyed by normalised title. `art` is a path under public/.
 *
 * The five gem packages share one still deliberately: they are the same
 * product at five quantities, and giving each its own picture would say they
 * were different things.
 *
 * Faces are spread across the families on purpose — a shop that sells police
 * jobs, racing bars and character models should not speak in one voice. What
 * holds the set together is the layout, the palette and the light. */
const GEMS = '/img/hero/gems.webp';

const CARDS = {
  'radio-station-framework': {
    art: '/img/hero/radio-station-framework.webp',
    blurb: 'A live DJ desk and broadcast system. Schedule shows, take listener calls, and stream to every vehicle radio in the city.',
    face: 'tall',
  },
  'paddock-racing-bar-v2': {
    art: '/img/hero/paddock-racing-bar-v2.webp',
    blurb: 'A racing bar MLO built as a social hub — where crews meet, plan and settle up. Standalone, optimised, no dependencies.',
    face: 'neutral',
  },

  /* ---- maps ---- */
  'arresto-police-cafe': { align: 'right',
    art: '/img/hero/arresto-police-cafe.webp',
    blurb: 'A police-themed cafe MLO beside MRPD — the off-duty room your officers actually use.',
    face: 'serif',
  },
  'rebel-radio': {
    art: '/img/hero/rebel-radio.webp',
    blurb: 'A multi-location radio station MLO built for media roleplay, from the booth to the transmitter.',
    face: 'tall',
  },

  /* ---- law enforcement ---- */
  'police-operations-bundle': {
    art: '/img/hero/police-operations-bundle.webp',
    blurb: 'Police Job, Radar Pursuit and Advanced Dispatch in one package. Everything a department needs, already wired together.',
    face: 'grotesque',
  },
  'modular-police-job': { align: 'right',
    art: '/img/hero/modular-police-job.webp',
    blurb: 'A multi-department police job with tactical systems, forensics and a full prison flow.',
    face: 'grotesque',
  },
  'advanced-dispatch-system': {
    art: '/img/hero/advanced-dispatch-system.webp',
    blurb: 'A dispatch command centre for live 911 calls, unit coordination and scene control.',
    face: 'grotesque',
  },
  'radar-pursuit-system': { align: 'right', band: 'top',
    art: '/img/hero/radar-pursuit-system.webp',
    blurb: 'An in-car radar that reads the road and keeps a pursuit balanced instead of one-sided.',
    face: 'condensed',
  },
  'wiretap-surveillance-system': {
    art: '/img/hero/wiretap-surveillance-system.webp',
    blurb: 'Plantable bugs, a live audio command centre and signal tracing — covert work that plays out in real time.',
    face: 'geometric',
  },

  /* ---- vehicles ---- */
  'premium-dealership': {
    art: '/img/hero/premium-dealership.webp',
    blurb: 'A dealership with financing, trade-ins, staff, stock and delivery — a business to run, not a menu to click.',
    face: 'serif',
  },
  'advanced-car-rental': {
    art: '/img/hero/advanced-car-rental.webp',
    blurb: 'Hourly rentals with VIP rates, extensions, late fees and persistent vehicles. Free, and complete.',
    face: 'geometric',
  },
  'advanced-boosting-system': { align: 'right',
    art: '/img/hero/advanced-boosting-system.webp',
    blurb: 'Progression-based vehicle boosting for servers that want the job to have a ladder.',
    face: 'grotesque',
  },
  'dynamic-vehicle-damage': {
    art: '/img/hero/dynamic-vehicle-damage.webp',
    blurb: 'Crashes that carry consequences — stalls, engine failure, limp mode, wheel and weapon damage, repair kits.',
    face: 'condensed',
  },

  /* ---- systems ---- */
  'complete-banking-billing-bundle': {
    art: '/img/hero/complete-banking-billing-bundle.webp',
    blurb: 'Accounts, invoices, loans, cheques and savings, with an optional LB Phone app.',
    face: 'geometric',
  },
  'dynamic-weather-v2': { align: 'right',
    art: '/img/hero/dynamic-weather-v2.webp',
    blurb: 'Weather and disasters that make each region of the map feel like its own place.',
    face: 'condensed',
  },
  'immersive-graffiti': {
    art: '/img/hero/immersive-graffiti.webp',
    blurb: 'A graffiti system with real-time sync and surface validation. Tags that stay where you put them.',
    face: 'tall',
  },
  'modern-chat': {
    art: '/img/hero/modern-chat.webp',
    blurb: 'A clean, quiet chat system that gets out of the way of the roleplay.',
    face: 'geometric',
  },
  'modern-radial-menu': {
    art: '/img/hero/modern-radial-menu.webp',
    blurb: 'A light, fully customisable radial menu. Free, and built to the same standard as everything else here.',
    face: 'neutral',
  },
  '20x-minigames-pack': { align: 'right',
    art: '/img/hero/20x-minigames-pack.webp',
    blurb: 'Twenty-plus standalone hacking and puzzle minigames, ready to drop into any job.',
    face: 'rounded',
  },
  'bomb-defusal': { align: 'right',
    art: '/img/hero/bomb-defusal.webp',
    blurb: 'A co-op defusal scenario: one player works the device, the other reads the manual.',
    face: 'grotesque',
  },
  'nyotaimori-dining-experience': {
    art: '/img/hero/nyotaimori-dining-experience.webp',
    /* The catalogue name carries its own explanation. On a card the name has
       to be a name — the explaining belongs in the line underneath. */
    title: 'Nyotaimori',
    blurb: 'A complete dining experience: build custom serving setups, plate from inventory, and use NPCs or real players as live models.',
    face: 'serif',
  },
  'global-fantasy-football': { band: 'top',
    art: '/img/hero/global-fantasy-football.webp',
    blurb: 'Animated pack openings, collectible player cards, squad building and cross-server matches.',
    face: 'geometric',
  },

  /* ---- gem packages: one product, five quantities, one picture ---- */
  '50-gems': { art: GEMS, blurb: 'Fifty gems for Global Fantasy Football — packs, upgrades and entries.', face: 'rounded' },
  '100-gems': { art: GEMS, blurb: 'One hundred gems for Global Fantasy Football — packs, upgrades and entries.', face: 'rounded' },
  '250-gems': { art: GEMS, blurb: 'Two hundred and fifty gems for Global Fantasy Football — packs, upgrades and entries.', face: 'rounded' },
  '500-gems': { art: GEMS, blurb: 'Five hundred gems for Global Fantasy Football — packs, upgrades and entries.', face: 'rounded' },
  '1000-gems': { art: GEMS, blurb: 'One thousand gems for Global Fantasy Football — packs, upgrades and entries.', face: 'rounded' },

  /* ---- peds ---- */
  'jake-nolan': {
    art: '/img/hero/jake-nolan.webp',
    blurb: 'Road Dog. A prospect carving out his place in the club, one job at a time.',
    face: 'serifQuiet',
  },
  'reggie-carter-80': {
    art: '/img/hero/reggie-carter-80.webp',
    blurb: 'Minimal, violent, impact. Eighty per cent of the work done before he says a word.',
    face: 'serifQuiet',
  },
  'malik-turner': {
    art: '/img/hero/malik-turner.webp',
    blurb: 'No Face. Blackout, ruthless, blood — a model built for the ugly end of the story.',
    face: 'serifQuiet',
  },
  'javier-morales': {
    art: '/img/hero/javier-morales.webp',
    blurb: 'La Sombra. Silent, unseen, predator — the one nobody remembers being there.',
    face: 'serifQuiet',
  },
  'terrence-shaw': {
    art: '/img/hero/terrence-shaw.webp',
    blurb: 'Lowkey. Calm, observant, invisible until it matters.',
    face: 'serifQuiet',
  },
};

/* Products still waiting for their own still keep the catalogue thumbnail, but
   they should not all fall back to the same face either. Matching on a keyword
   gives a newly published package a sensible voice without anyone editing a
   list, and the hash keeps anything unmatched stable and spread out. */
const FACE_RULES = [
  [/\bradio\b|broadcast|\bdj\b/i, 'tall'],
  [/police|dispatch|arresto|arrest|pursuit|radar/i, 'grotesque'],
  [/racing|paddock|boost|drift|speed/i, 'grotesque'],
  [/bank|billing|wiretap|surveillance|chat|menu/i, 'geometric'],
  [/gem|minigame|weather|rain/i, 'rounded'],
  /* The hospitality and luxury-goods end of the catalogue takes the serif. */
  [/dining|cafe|restaurant|nyotaimori|dealership|premium/i, 'serif'],
];

const hash = (text) => {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const SPREAD = ['condensed', 'grotesque', 'tall', 'serifQuiet', 'geometric'];

/* The ultra-condensed face only works on a short name.
 *
 * It is drawn to be set enormous, and enormous is what earns it — "REBEL
 * RADIO" fills a card and looks like a poster. Give it "RADIO STATION
 * FRAMEWORK" and the size has to come down to fit, at which point the
 * extreme condensation stops reading as a choice and starts reading as
 * squashed. Anything past this length falls back to the neutral sans, set
 * light and tightly tracked — the same treatment the hero headline uses. */
const CONDENSED_MAX_CHARS = 14;
const CONDENSED_FACES = new Set(['tall', 'condensed']);

const applyLengthRule = (name, title) => {
  if (CONDENSED_FACES.has(name) && String(title || '').trim().length > CONDENSED_MAX_CHARS) {
    return FACES.neutral;
  }
  return FACES[name];
};

export const faceForProduct = (product) => {
  const key = normalise(product?.title);
  const title = String(product?.title || '');
  const entry = CARDS[key];
  if (entry?.face) return applyLengthRule(entry.face, entry.title || title);

  for (const [pattern, face] of FACE_RULES) {
    if (pattern.test(title)) return applyLengthRule(face, title);
  }
  return applyLengthRule(SPREAD[hash(key || 'x') % SPREAD.length], title);
};

export const heroCardFor = (product) => CARDS[normalise(product?.title)] || null;

export const heroCardCount = Object.keys(CARDS).length;

export default CARDS;
